import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistanceRequestListItem, AssistanceStatus, AssistanceUrgency } from '@tsr/shared-types';
import { fetchAssistanceRequests } from './api';
import { inboundRequestorLabel } from './requestsTabVocabulary';

/**
 * Standing ASSIST queue list for the operations panel (#197).
 *
 * The COP surfaces assistance via a command-bar dropdown (`AssistanceControls`);
 * the operations panel instead gives the queue a *protected, always-visible*
 * column (spec §2 — never compressed for media). This is the read surface only —
 * creating / routing requests stays on the COP — so the panel can be a glanceable
 * second screen. Rows are ranked open-first, most-urgent-first, and the list
 * self-refreshes on a fixed cadence.
 */

const REFRESH_MS = 30_000;

const URGENCY_RANK: Record<AssistanceUrgency, number> = { immediate: 0, urgent: 1, routine: 2 };

/** Urgency accent (text + border tailwind classes) — shared with the Coordination
 *  → Assist worklist (#378) so both assist surfaces read the same urgency colour. */
export const ASSIST_URGENCY_TONE: Record<AssistanceUrgency, string> = {
  immediate: 'text-rose-300 border-rose-500/60',
  urgent: 'text-amber-300 border-amber-500/60',
  routine: 'text-slate-300 border-slate-600',
};

/** Humanized urgency label (W4a / council B) — no raw enum value renders on a row.
 *  `Record<AssistanceUrgency, …>` forces a label for any new urgency literal. */
export const ASSIST_URGENCY_LABEL: Record<AssistanceUrgency, string> = {
  immediate: 'Immediate',
  urgent: 'Urgent',
  routine: 'Routine',
};

/** Lifecycle status → dot colour — shared with the Coordination → Assist worklist. */
export const ASSIST_STATUS_DOT: Record<string, string> = {
  submitted: 'bg-sky-400',
  triaged: 'bg-cyan-400',
  routed: 'bg-indigo-400',
  assigned: 'bg-violet-400',
  in_progress: 'bg-amber-400',
  fulfilled: 'bg-emerald-400',
  closed: 'bg-slate-500',
  cancelled: 'bg-rose-500',
};

/** The ordered (non-cancelled) assist lifecycle — the 7-state happy path. Single
 *  source for the stepper order, shared by the detail-modal stepper
 *  (AssistanceRequestDetail) and the Coordination → Assist worklist stepper
 *  (AssistQueuePanel). `cancelled` is an off-path side-exit, not a linear step. */
export const ASSIST_STATUS_ORDER: readonly AssistanceStatus[] = [
  'submitted',
  'triaged',
  'routed',
  'assigned',
  'in_progress',
  'fulfilled',
  'closed',
];

/** Display label for every assist status (incl. the off-path `cancelled`). The
 *  `Record<AssistanceStatus, …>` type forces a label for any new status added to
 *  the shared-types enum — keeping the three assist surfaces in lockstep. */
export const ASSIST_STATUS_LABEL: Record<AssistanceStatus, string> = {
  submitted: 'Submitted',
  triaged: 'Triaged',
  routed: 'Routed',
  assigned: 'Assigned',
  in_progress: 'In progress',
  fulfilled: 'Fulfilled',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

const TERMINAL = new Set(['fulfilled', 'closed', 'cancelled']);

/** Open-first, then most-urgent-first — the triage order an operator scans. */
export function sortAssistRows(rows: AssistanceRequestListItem[]): AssistanceRequestListItem[] {
  return rows.slice().sort((a, b) => {
    const aOpen = TERMINAL.has(a.status) ? 1 : 0;
    const bOpen = TERMINAL.has(b.status) ? 1 : 0;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
  });
}

/** Count of non-terminal (still-actionable) requests — the badge number. */
export function openAssistCount(rows: AssistanceRequestListItem[]): number {
  return rows.filter((r) => !TERMINAL.has(r.status)).length;
}

export function AssistQueueList(): JSX.Element {
  const [rows, setRows] = useState<AssistanceRequestListItem[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback((signal?: AbortSignal) => {
    void fetchAssistanceRequests({ limit: 50 }, signal)
      .then((r) => setRows(sortAssistRows(r.requests)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    refresh(ctl.signal);
    timer.current = setInterval(() => refresh(), REFRESH_MS);
    return () => {
      ctl.abort();
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh]);

  const open = openAssistCount(rows);

  return (
    <section
      data-testid="assist-queue"
      className="flex h-full flex-col overflow-hidden border border-slate-700 bg-slate-900/80 rounded-lg"
    >
      <header className="flex items-center justify-between border-b border-slate-700 px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-200">Assist Queue</h2>
        <span className="rounded-full bg-violet-600/80 px-2 py-0.5 text-[11px] font-semibold text-white">
          {open}
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="px-3 py-4 text-[12px] text-slate-400">No assistance requests.</p>
        ) : (
          <ul className="divide-y divide-slate-800">
            {rows.map((r) => (
              <li key={r.id} className="px-3 py-2" data-testid="assist-row">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${ASSIST_STATUS_DOT[r.status] ?? 'bg-slate-500'}`}
                    title={ASSIST_STATUS_LABEL[r.status] ?? r.status}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-slate-100">
                    {inboundRequestorLabel(r.requestor_name)}
                  </span>
                  {/* #419 closed-loop — the PEOC receipt indicator (compact glyph; full
                      time in the tooltip), so the queue shows what's been acknowledged. */}
                  {r.acknowledged_at && (
                    <span
                      data-testid="assist-row-received"
                      className="shrink-0 text-[10px] text-emerald-400"
                      title={`Received by PEOC · ${r.acknowledged_at}`}
                    >
                      ✓
                    </span>
                  )}
                  <span
                    className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${ASSIST_URGENCY_TONE[r.urgency]}`}
                  >
                    {ASSIST_URGENCY_LABEL[r.urgency]}
                  </span>
                  {r.response_level != null && (
                    <span className="shrink-0 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-200">
                      L{r.response_level}
                    </span>
                  )}
                </div>
                {r.categories.length > 0 && (
                  <p className="mt-0.5 truncate pl-4 text-[11px] text-slate-400">
                    {r.categories.join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
